// /workspaces/mcn-youtube-platform/mcn-frontend/src/components/dashboards/AggregatorDashboard/index.js
import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/UserContext';
import { API_ENDPOINTS } from '../../../config';
import './AggregatorDashboard.css';
import AggregatorApplications from './AggregatorApplications';

// Import icons - using Unicode symbols as placeholders
const TrendingUpIcon = () => <span>📈</span>;
const UsersIcon = () => <span>👥</span>;
const DollarIcon = () => <span>💰</span>;
const StarIcon = () => <span>⭐</span>;
const CopyIcon = () => <span>📋</span>;
const CheckIcon = () => <span>✅</span>;
const ApplicationIcon = () => <span>📝</span>;

const AggregatorDashboard = ({ activeTab = 'overview' }) => {
  const { currentUser, getAggregatorTier, getCommissionRate, getTenantDisplayName } = useUser();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [currentTab, setCurrentTab] = useState(activeTab);
  const [copied, setCopied] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  // Get aggregator info with safe fallbacks
  const tier = typeof getAggregatorTier === 'function' ? getAggregatorTier() : 'BRONZE';
  const commissionRate = typeof getCommissionRate === 'function' ? getCommissionRate() : 5;
  const getTenantName = typeof getTenantDisplayName === 'function' ? getTenantDisplayName() : 'Choice Network';
  const referralCode = currentUser?.aggregatorProfile?.referralCode || 'AGG' + currentUser?.id;
  const referralLink = `${window.location.origin}/apply?ref=${referralCode}`;  // Changed from /register to /apply

  useEffect(() => {
    // Update currentTab when activeTab prop changes
    setCurrentTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    console.log('Starting fetchDashboardData...');
    console.log('API_ENDPOINTS.aggregator:', API_ENDPOINTS.aggregator);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      console.log('Making API calls to:', {
        stats: API_ENDPOINTS.aggregator?.stats || '/api/aggregator/stats',
        referrals: API_ENDPOINTS.aggregator?.referrals || '/api/aggregator/referrals',
        commissions: API_ENDPOINTS.aggregator?.commissions || '/api/aggregator/commissions'
      });

      // Fetch all data in parallel
      const [statsRes, referralsRes, commissionsRes] = await Promise.all([
        fetch(API_ENDPOINTS.aggregator?.stats || '/api/aggregator/stats', { headers }),
        fetch(API_ENDPOINTS.aggregator?.referrals || '/api/aggregator/referrals', { headers }),
        fetch(API_ENDPOINTS.aggregator?.commissions || '/api/aggregator/commissions', { headers })
      ]);
      
      console.log('Response statuses:', {
        stats: statsRes.status,
        referrals: referralsRes.status,
        commissions: commissionsRes.status
      });

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        console.log('Stats data:', statsData);
        setStats(statsData);
      } else {
        console.error('Stats fetch failed:', statsRes.status);
        setStats({
          totalReferrals: 0,
          activeReferrals: 0,
          monthlyReferrals: 0,
          totalEarnings: 0,
          monthlyEarnings: 0,
          availableBalance: 0,
          totalRevenue: 0,
          conversionRate: 0,
          totalApplications: 0,
          pendingApplications: 0,
          approvedApplications: 0
        });
      }

      if (referralsRes.ok) {
        const referralsData = await referralsRes.json();
        console.log('Referrals data:', referralsData);
        setReferrals(Array.isArray(referralsData) ? referralsData : []);
      } else {
        console.error('Referrals fetch failed:', referralsRes.status);
        setReferrals([]);
      }

      if (commissionsRes.ok) {
        const commissionsData = await commissionsRes.json();
        console.log('Commissions data:', commissionsData);
        setCommissions(Array.isArray(commissionsData) ? commissionsData : []);
      } else {
        console.error('Commissions fetch failed:', commissionsRes.status);
        setCommissions([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set default values on error
      setStats({
        totalReferrals: 0,
        activeReferrals: 0,
        monthlyReferrals: 0,
        totalEarnings: 0,
        monthlyEarnings: 0,
        availableBalance: 0,
        totalRevenue: 0,
        conversionRate: 0,
        totalApplications: 0,
        pendingApplications: 0,
        approvedApplications: 0
      });
      setReferrals([]);
      setCommissions([]);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
  };

  const handleWithdrawal = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (parseFloat(withdrawalAmount) > (stats?.availableBalance || 0)) {
      alert('Insufficient balance');
      return;
    }

    setWithdrawing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.aggregator?.withdraw || '/api/aggregator/withdraw', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: parseFloat(withdrawalAmount) })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Withdrawal request submitted successfully');
        setWithdrawalAmount('');
        fetchDashboardData();
      } else {
        const error = await response.json();
        alert(error.error || 'Withdrawal request failed');
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      alert('Error processing withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const getTierColor = (tierName) => {
    const colors = {
      'BRONZE': '#CD7F32',
      'SILVER': '#C0C0C0',
      'GOLD': '#FFD700',
      'PLATINUM': '#E5E4E2'
    };
    return colors[tierName] || '#666';
  };

  const getTierTargets = (currentTier) => {
    const targets = {
      'BRONZE': { next: 'SILVER', referrals: 10, revenue: 50000 },
      'SILVER': { next: 'GOLD', referrals: 25, revenue: 200000 },
      'GOLD': { next: 'PLATINUM', referrals: 50, revenue: 500000 },
      'PLATINUM': { next: null, referrals: null, revenue: null }
    };
    return targets[currentTier] || targets['BRONZE'];
  };

  const tierTargets = getTierTargets(tier);
  const progressToNextTier = tierTargets.next ? {
    referrals: Math.min(100, ((stats?.totalReferrals || 0) / tierTargets.referrals) * 100),
    revenue: Math.min(100, ((stats?.totalRevenue || 0) / tierTargets.revenue) * 100)
  } : null;

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  // If activeTab is 'applications', show only the AggregatorApplications component
  if (currentTab === 'applications') {
    return (
      <div className="aggregator-dashboard">
        <div className="dashboard-header">
          <h1>Aggregator Partner Dashboard - Applications</h1>
          <p className="tenant-name">{getTenantName}</p>
        </div>
        
        <div className="dashboard-tabs">
          <button 
            className="tab-btn"
            onClick={() => setCurrentTab('overview')}
          >
            ← Back to Overview
          </button>
        </div>
        
        <AggregatorApplications />
      </div>
    );
  }

  return (
    <div className="aggregator-dashboard">
      <div className="dashboard-header">
        <h1>Aggregator Partner Dashboard</h1>
        <p className="tenant-name">{getTenantName}</p>
      </div>

      {/* Tier Badge */}
      <div className="tier-section">
        <div className="tier-badge" style={{ borderColor: getTierColor(tier) }}>
          <StarIcon />
          <span className="tier-name" style={{ color: getTierColor(tier) }}>{tier} TIER</span>
          <span className="commission-rate">{commissionRate}% Commission</span>
        </div>
        
        {progressToNextTier && (
          <div className="tier-progress">
            <h3>Progress to {tierTargets.next} Tier</h3>
            <div className="progress-item">
              <span>Referrals: {stats?.totalReferrals || 0} / {tierTargets.referrals}</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progressToNextTier.referrals}%` }}></div>
              </div>
            </div>
            <div className="progress-item">
              <span>Revenue: ₹{(stats?.totalRevenue || 0).toLocaleString()} / ₹{tierTargets.revenue.toLocaleString()}</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progressToNextTier.revenue}%` }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards - Added Applications Card */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><UsersIcon /></div>
          <div className="stat-content">
            <h3>Total Referrals</h3>
            <p className="stat-value">{stats?.totalReferrals || 0}</p>
            <span className="stat-change positive">+{stats?.monthlyReferrals || 0} this month</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><ApplicationIcon /></div>
          <div className="stat-content">
            <h3>Applications</h3>
            <p className="stat-value">{stats?.totalApplications || 0}</p>
            <span className="stat-detail">{stats?.pendingApplications || 0} pending</span>
            <button 
              className="view-btn"
              onClick={() => setCurrentTab('applications')}
              style={{ marginTop: '10px', fontSize: '0.9em', padding: '5px 10px', background: '#667eea', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
            >
              View Applications
            </button>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><DollarIcon /></div>
          <div className="stat-content">
            <h3>Total Earnings</h3>
            <p className="stat-value">₹{(stats?.totalEarnings || 0).toLocaleString()}</p>
            <span className="stat-change positive">+₹{(stats?.monthlyEarnings || 0).toLocaleString()} this month</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><TrendingUpIcon /></div>
          <div className="stat-content">
            <h3>Active Referrals</h3>
            <p className="stat-value">{stats?.activeReferrals || 0}</p>
            <span className="stat-detail">{stats?.conversionRate || 0}% conversion rate</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><DollarIcon /></div>
          <div className="stat-content">
            <h3>Available Balance</h3>
            <p className="stat-value">₹{(stats?.availableBalance || 0).toLocaleString()}</p>
            <button 
              className="withdraw-btn"
              onClick={() => setCurrentTab('withdraw')}
            >
              Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* Referral Link Section - Updated to use /apply route */}
      <div className="referral-link-section">
        <h2>Your Referral Link</h2>
        <p style={{ marginBottom: '10px', color: '#666' }}>
          Share this link with creators to earn commission when they join!
        </p>
        <div className="referral-link-container">
          <input 
            type="text" 
            value={referralLink} 
            readOnly 
            className="referral-link-input"
          />
          <button 
            className="copy-btn"
            onClick={copyReferralLink}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <p className="referral-code">Referral Code: <strong>{referralCode}</strong></p>
      </div>

      {/* Tabs - Added Applications Tab */}
      <div className="dashboard-tabs">
        <button 
          className={`tab-btn ${currentTab === 'overview' ? 'active' : ''}`}
          onClick={() => setCurrentTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${currentTab === 'applications' ? 'active' : ''}`}
          onClick={() => setCurrentTab('applications')}
        >
          Applications
        </button>
        <button 
          className={`tab-btn ${currentTab === 'referrals' ? 'active' : ''}`}
          onClick={() => setCurrentTab('referrals')}
        >
          Referrals
        </button>
        <button 
          className={`tab-btn ${currentTab === 'commissions' ? 'active' : ''}`}
          onClick={() => setCurrentTab('commissions')}
        >
          Commissions
        </button>
        <button 
          className={`tab-btn ${currentTab === 'withdraw' ? 'active' : ''}`}
          onClick={() => setCurrentTab('withdraw')}
        >
          Withdraw
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {currentTab === 'overview' && (
          <div className="overview-section">
            <h2>Quick Actions</h2>
            <div className="quick-actions">
              <button 
                className="action-btn"
                onClick={() => setCurrentTab('applications')}
              >
                <ApplicationIcon /> View Applications
              </button>
              <button 
                className="action-btn"
                onClick={() => window.open('/apply?ref=' + referralCode, '_blank')}
              >
                <UsersIcon /> Preview Application Form
              </button>
              <button 
                className="action-btn"
                onClick={copyReferralLink}
              >
                <CopyIcon /> Copy Referral Link
              </button>
            </div>
          </div>
        )}

        {currentTab === 'applications' && (
          <AggregatorApplications />
        )}

        {currentTab === 'referrals' && (
          <div className="referrals-section">
            <h2>Your Referrals</h2>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Type</th>
                    <th>Joined Date</th>
                    <th>Status</th>
                    <th>Revenue Generated</th>
                    <th>Your Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.length > 0 ? referrals.map((referral, index) => (
                    <tr key={referral.id || index}>
                      <td>{referral.username || referral.email}</td>
                      <td><span className="role-badge">{referral.role}</span></td>
                      <td>{new Date(referral.createdAt).toLocaleDateString()}</td>
                      <td>
                        <span className={`status-badge ${(referral.status || 'Active').toLowerCase()}`}>
                          {referral.status || 'Active'}
                        </span>
                      </td>
                      <td>₹{(referral.revenueGenerated || 0).toLocaleString()}</td>
                      <td className="commission-amount">
                        ₹{((referral.revenueGenerated || 0) * commissionRate / 100).toLocaleString()}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="6" className="no-data">No referrals yet. Share your link to get started!</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {currentTab === 'commissions' && (
          <div className="commissions-section">
            <h2>Commission History</h2>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Referral</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.length > 0 ? commissions.map((commission, index) => (
                    <tr key={commission.id || index}>
                      <td>{new Date(commission.createdAt).toLocaleDateString()}</td>
                      <td>{commission.description}</td>
                      <td>{commission.referralUsername}</td>
                      <td className="commission-amount">₹{(commission.amount || 0).toLocaleString()}</td>
                      <td>
                        <span className={`status-badge ${(commission.status || 'Pending').toLowerCase()}`}>
                          {commission.status}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="no-data">No commissions yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {currentTab === 'withdraw' && (
          <div className="withdraw-section">
            <h2>Withdraw Funds</h2>
            <div className="withdraw-card">
              <div className="balance-info">
                <p>Available Balance</p>
                <h3>₹{(stats?.availableBalance || 0).toLocaleString()}</h3>
              </div>
              
              <div className="withdraw-form">
                <label>Amount to Withdraw</label>
                <input 
                  type="number"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  placeholder="Enter amount"
                  max={stats?.availableBalance || 0}
                  min="100"
                />
                <p className="withdraw-note">Minimum withdrawal: ₹100</p>
                
                <button 
                  className="btn-withdraw"
                  onClick={handleWithdrawal}
                  disabled={withdrawing || !withdrawalAmount || parseFloat(withdrawalAmount) < 100}
                >
                  {withdrawing ? 'Processing...' : 'Request Withdrawal'}
                </button>
              </div>

              <div className="payment-methods">
                <h4>Payment Method</h4>
                <p>Funds will be transferred to your registered bank account within 3-5 business days.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AggregatorDashboard;